// =====================================================================
// useCreditPreflight — hook that owns the pre-flight modal and exposes
// a single `ensure(job)` function returning a Promise<boolean>.
//
// Usage in a page:
//   const { ensure, dialog } = useCreditPreflight(workspaceId);
//   ...
//   if (!(await ensure("brief_draft"))) return;
//   ...
//   return <>{...page}{dialog}</>;
// =====================================================================

import { useCallback, useRef, useState } from "react";
import {
  decidePreflight,
  fetchPreflightStats,
  isPreflightSkipped,
  markPreflightSkipped,
  type PreflightDecision,
} from "@/lib/aiCreditPreflight";
import { loadCreditEstimates, type AiJobKind } from "@/lib/aiCreditEstimates";
import { CreditPreflightDialog } from "@/components/composer/CreditPreflightDialog";

type Pending = {
  job: AiJobKind;
  decision: Extract<PreflightDecision, { ok: false }>;
  resolve: (ok: boolean) => void;
};

export const useCreditPreflight = (workspaceId: string | null | undefined) => {
  const [pending, setPending] = useState<Pending | null>(null);
  // Guard against double-open if the user fires two jobs back-to-back.
  const inFlight = useRef(false);

  const ensure = useCallback(
    async (job: AiJobKind): Promise<boolean> => {
      if (!workspaceId) return true;
      if (isPreflightSkipped(workspaceId, job)) return true;

      // Warm DB-backed credit-estimate cache so decidePreflight uses live
      // values rather than the hardcoded fallback. Fire-and-forget so we
      // don't block the courtesy check on a slow read.
      void loadCreditEstimates();

      const stats = await fetchPreflightStats(workspaceId);
      const decision = decidePreflight(stats, job);
      if (decision.ok) return true;
      const shortfall = decision as Extract<PreflightDecision, { ok: false }>;

      // Already showing one — don't stack.
      if (inFlight.current) return false;
      inFlight.current = true;

      return new Promise<boolean>((resolve) => {
        setPending({
          job,
          decision: shortfall,
          resolve: (ok) => {
            inFlight.current = false;
            resolve(ok);
          },
        });
      });
    },
    [workspaceId],
  );

  const handleCancel = useCallback(() => {
    if (!pending) return;
    pending.resolve(false);
    setPending(null);
  }, [pending]);

  const handleContinue = useCallback(() => {
    if (!pending) return;
    if (workspaceId) markPreflightSkipped(workspaceId, pending.job);
    pending.resolve(true);
    setPending(null);
  }, [pending, workspaceId]);

  const dialog = (
    <CreditPreflightDialog
      open={pending !== null}
      job={pending?.job ?? null}
      decision={pending?.decision ?? null}
      onCancel={handleCancel}
      onContinue={handleContinue}
    />
  );

  return { ensure, dialog };
};
